// Dropship Shopee untuk MITRA (reseller) — helper MURNI (tanpa I/O) supaya bisa
// diuji unit. Sisi server ada di dropshipShopeeServer.ts + dropshipShopeeAuth.ts,
// layarnya src/components/dropship/*.
//
// ── APA FITURNYA ─────────────────────────────────────────────────────────────
// Mitra (pelanggan toko yang login di storefront) mengunduh produk ready stock
// toko dalam format Mass Upload Shopee untuk diunggah ke toko Shopee-nya
// SENDIRI, dengan harga = HARGA NORMAL toko + markup % pilihannya; lalu
// memperbarui stok toko Shopee-nya lewat file "Perbarui Info Penjualan" yang
// diisi stok bebas website (mekanisme Stok Opname Shopee admin, mig 4630).
//
// ── KEPUTUSAN YANG MENGIKAT ──────────────────────────────────────────────────
// 1. Basis harga = `product_variants.price_normal` (harga umum yang juga tampil
//    di Katalog Reseller & export admin) — BUKAN harga beli mitra, BUKAN promo
//    kampanye. Markup ditumpuk di atasnya lewat `ShopeeBuildOptions.priceTransform`.
// 2. Markup bilangan bulat 0..DROPSHIP_MARKUP_MAX. Markup 0 = harga normal apa
//    adanya (TANPA pembulatan — mitra yang ingin mencerminkan harga toko tak
//    boleh melihat 85.500 berubah jadi 86.000).
// 3. Pembulatan KE ATAS: Rp 1.000 untuk harga ≥ Rp 20.000, Rp 100 di bawahnya —
//    supaya rasio termahal/termurah 7× Shopee tidak jebol pada produk murah.
// 4. Stok yang dikirim ke mitra = max(0, stok bebas − cadangan toko). Satu
//    fungsi (`resellerVisibleStock`) dipakai export DAN update stok.
// 5. Batas mitra LEBIH RENDAH dari admin: 200 produk/unduhan — proxy nginx
//    Hostinger menyerah ±60 detik (GOTCHA #19) dan dryRun menggandakan kerja.
// 6. Template Mass Upload WAJIB milik mitra (2026-09-24) — template toko tidak
//    pernah dipakai di jalur mitra, walau tokonya punya. Template Shopee membawa
//    kolom jasa kirim `channel_id.*` milik toko yang MENGUNDUHNYA, dan export
//    mengisi semuanya "Aktif": file dari template toko menyalakan jasa kirim
//    toko, bukan milik toko Shopee mitra — sumber error ketidaksesuaian template
//    saat upload. Ditegakkan di route (400 `needsTemplate`), bukan cuma di layar.

import { shopeeVariantPrice, type ShopeeVariantRow } from "@/lib/shopeeExport";

// ── Konstanta ────────────────────────────────────────────────────────────────

export const DROPSHIP_MARKUP_MIN = 0;
export const DROPSHIP_MARKUP_MAX = 300;
/** Chip pilihan cepat di layar; 20 terisi saat pertama kali dipakai. */
export const DROPSHIP_MARKUP_PRESETS = [10, 15, 20, 25, 30] as const;
export const DROPSHIP_DEFAULT_MARKUP = 20;

/** Di bawah ambang ini pembulatan Rp 100, selebihnya Rp 1.000. */
export const DROPSHIP_ROUND_SMALL_BELOW = 20_000;
export const DROPSHIP_ROUND_SMALL = 100;
export const DROPSHIP_ROUND_LARGE = 1_000;

/**
 * Kalimat pembulatan yang dipakai SEMUA layar (bantuan, bilah aksi, kartu admin,
 * tutorial) — diturunkan dari konstanta supaya tak ada dua penjelasan yang
 * berbeda dari kodenya.
 */
export const DROPSHIP_ROUNDING_LABEL = `dibulatkan ke atas ke Rp ${DROPSHIP_ROUND_LARGE.toLocaleString("id-ID")} (di bawah Rp ${DROPSHIP_ROUND_SMALL_BELOW.toLocaleString("id-ID")} ke Rp ${DROPSHIP_ROUND_SMALL})`;

/** Produk per unduhan mitra (bukan batas Shopee; lihat keputusan 5). */
export const DROPSHIP_MAX_PRODUCTS_PER_EXPORT = 200;
/** Unduhan file produk per mitra per 24 jam — dihitung dari dropship_shopee_logs di RPC. */
export const DROPSHIP_DAILY_QUOTA = 40;
/** Halaman daftar produk di layar mitra. */
export const DROPSHIP_PRODUCTS_PAGE_SIZE = 50;
/** Cadangan stok maksimum yang masuk akal (pelindung salah ketik di setelan). */
export const DROPSHIP_STOCK_BUFFER_MAX = 100_000;

/** Rate limit in-memory per proses (best-effort; kuota harian ada di DB). */
export const DROPSHIP_RATE_LIMITS = {
  access: { limit: 120, windowMs: 10 * 60_000 },
  products: { limit: 90, windowMs: 10 * 60_000 },
  preview: { limit: 30, windowMs: 10 * 60_000 },
  download: { limit: 10, windowMs: 10 * 60_000 },
  stock: { limit: 10, windowMs: 10 * 60_000 },
  ip: { limit: 240, windowMs: 10 * 60_000 },
} as const;

export type DropshipRateKind = Exclude<keyof typeof DROPSHIP_RATE_LIMITS, "ip">;

/** Awalan nama unduhan: `dropship-shopee-produk-markup20-2026-09-02.xlsx`. */
export function dropshipFilePrefix(markupPct: number): string {
  return `dropship-shopee-produk-markup${Math.max(0, Math.round(markupPct))}`;
}

/** Kunci localStorage per toko: markup terakhir + tab terakhir. */
export function dropshipPrefKey(storeId: string): string {
  return `dropship-shopee:${storeId}`;
}

// ── Harga ────────────────────────────────────────────────────────────────────

/** Langkah pembulatan untuk sebuah harga hasil markup. */
export function dropshipRoundStep(price: number): number {
  return price < DROPSHIP_ROUND_SMALL_BELOW ? DROPSHIP_ROUND_SMALL : DROPSHIP_ROUND_LARGE;
}

/**
 * Harga jual di Shopee mitra: harga normal + markup %, dibulatkan KE ATAS ke
 * kelipatan `dropshipRoundStep`. Markup 0 memulangkan harga normal (dibulatkan
 * ke rupiah bulat saja). Input tak wajar (NaN/≤0) → 0, yang oleh export
 * dilaporkan sebagai `harga_kosong`, bukan ditulis.
 */
export function applyDropshipMarkup(base: number, markupPct: number): number {
  if (!Number.isFinite(base) || base <= 0) return 0;
  const pct = clampMarkup(markupPct);
  if (pct === 0) return Math.round(base);
  const raw = base * (1 + pct / 100);
  const step = dropshipRoundStep(raw);
  return Math.ceil(raw / step - 1e-9) * step;
}

/** Potong markup ke rentang sah; NaN → 0. */
export function clampMarkup(markupPct: number): number {
  if (!Number.isFinite(markupPct)) return DROPSHIP_MARKUP_MIN;
  return Math.min(DROPSHIP_MARKUP_MAX, Math.max(DROPSHIP_MARKUP_MIN, Math.round(markupPct)));
}

/**
 * Baca input markup dari form/query: bilangan bulat 0..300. Selain itu null —
 * pemanggil menjawab 400, bukan diam-diam memakai angka lain.
 */
export function parseMarkupInput(raw: unknown): number | null {
  const text = typeof raw === "number" ? String(raw) : String(raw ?? "").trim();
  if (!/^\d{1,3}$/.test(text)) return null;
  const n = Number.parseInt(text, 10);
  if (n < DROPSHIP_MARKUP_MIN || n > DROPSHIP_MARKUP_MAX) return null;
  return n;
}

/** Markup efektif = yang diminta mitra, tak boleh di bawah minimum toko. */
export function resolveMarkup(requested: number, storeMin: number): number {
  return Math.max(clampMarkup(requested), clampMarkup(storeMin));
}

/** Setelan `dropship_shopee_markup_min` mentah → 0..300 (fallback 0). */
export function normalizeDropshipMarkupMin(raw: string | null | undefined): number {
  const n = parseMarkupInput((raw ?? "").trim());
  return n ?? 0;
}

// ── Stok ─────────────────────────────────────────────────────────────────────

/** Setelan `dropship_shopee_stock_buffer` mentah → 0..DROPSHIP_STOCK_BUFFER_MAX. */
export function normalizeDropshipStockBuffer(raw: string | null | undefined): number {
  const n = Number.parseInt((raw ?? "").trim(), 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(DROPSHIP_STOCK_BUFFER_MAX, n);
}

/**
 * Stok yang boleh dilihat/dijual mitra: stok bebas dikurangi cadangan toko,
 * tak pernah negatif. Dipakai export (`stockTransform`) dan update stok.
 */
export function resellerVisibleStock(freeStock: number, buffer: number): number {
  const stock = Number.isFinite(freeStock) ? freeStock : 0;
  const keep = Number.isFinite(buffer) ? Math.max(0, buffer) : 0;
  return Math.max(0, Math.round(stock - keep));
}

// ── Akses (cermin RPC dropship_shopee_access) ────────────────────────────────

export type DropshipAccess = {
  /** Toggle toko `dropship_shopee_enabled` menyala. */
  enabled: boolean;
  /** Ada baris customers untuk auth.uid() di toko ini. */
  isCustomer: boolean;
  /** `customers.status = 'active'`. */
  isActive: boolean;
  /** Punya grup mitra aktif (`group_id` terisi & `reseller_groups.is_active`). */
  isMitra: boolean;
  /** Setelan toko: hanya mitra bergrup yang boleh. */
  mitraOnly: boolean;
  customerId: string | null;
  storeId: string | null;
  storeName: string;
  stockBuffer: number;
  markupMin: number;
  /** Unduhan produk 24 jam terakhir (kuota harian). */
  todayExports: number;
  /** Riwayat terakhir untuk kartu "Terakhir diunduh". */
  lastExportAt: string | null;
  lastStockAt: string | null;
};

export type DropshipAccessState =
  | "ok"
  | "disabled"
  | "not_customer"
  | "not_active"
  | "not_mitra";

/** Bentuk baris JSON RPC → objek bertipe. Nilai hilang → paling ketat. */
export function mapDropshipAccessRow(raw: unknown): DropshipAccess {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const bool = (k: string) => r[k] === true;
  const str = (k: string) => (typeof r[k] === "string" ? (r[k] as string) : null);
  const num = (k: string) => {
    const v = Number(r[k]);
    return Number.isFinite(v) ? v : 0;
  };
  return {
    enabled: bool("enabled"),
    isCustomer: bool("is_customer"),
    isActive: bool("is_active"),
    isMitra: bool("is_mitra"),
    mitraOnly: r["mitra_only"] === undefined ? true : bool("mitra_only"),
    customerId: str("customer_id"),
    storeId: str("store_id"),
    storeName: str("store_name") ?? "",
    stockBuffer: normalizeDropshipStockBuffer(String(r["stock_buffer"] ?? "")),
    markupMin: normalizeDropshipMarkupMin(String(r["markup_min"] ?? "")),
    // `has_store_template`/`template_uploaded_at` dari RPC sengaja tak dipetakan:
    // jalur mitra tak memakai template toko (keputusan 6).
    todayExports: Math.max(0, Math.round(num("today_exports"))),
    lastExportAt: str("last_export_at"),
    lastStockAt: str("last_stock_at"),
  };
}

/**
 * Keadaan layar dari hasil RPC. Urutannya = urutan pesan yang paling berguna:
 * fitur mati lebih dulu (tak ada gunanya menyuruh daftar jadi mitra kalau
 * tokonya belum menyalakan fiturnya).
 */
export function dropshipAccessState(a: DropshipAccess): DropshipAccessState {
  if (!a.enabled) return "disabled";
  if (!a.isCustomer) return "not_customer";
  if (!a.isActive) return "not_active";
  if (a.mitraOnly && !a.isMitra) return "not_mitra";
  return "ok";
}

/** Pesan per keadaan (dipakai route untuk 403 dan layar untuk kartu). */
export const DROPSHIP_STATE_COPY: Record<Exclude<DropshipAccessState, "ok">, string> = {
  disabled: "Fitur Dropship Shopee belum diaktifkan oleh toko ini.",
  not_customer: "Akun ini bukan pelanggan toko ini.",
  not_active: "Akunmu belum aktif. Hubungi admin toko dulu ya.",
  not_mitra: "Dropship Shopee khusus mitra aktif (Reseller/Agen).",
};

/** Kuota harian tersisa (tak pernah negatif). */
export function dropshipQuotaLeft(access: Pick<DropshipAccess, "todayExports">): number {
  return Math.max(0, DROPSHIP_DAILY_QUOTA - access.todayExports);
}

// ── Permintaan export ────────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type DropshipExportRequest = {
  productIds: string[];
  markupPct: number;
  dryRun: boolean;
};

export type DropshipExportParse =
  | { ok: true; value: DropshipExportRequest }
  | { ok: false; error: string };

/**
 * Validasi field multipart route export: `productIds` (JSON array uuid atau
 * daftar koma), `markup`, `dryRun`. Duplikat dibuang, urutan dipertahankan.
 */
export function parseDropshipExportRequest(fields: {
  productIds: unknown;
  markup: unknown;
  dryRun?: unknown;
}): DropshipExportParse {
  let rawIds: unknown = fields.productIds;
  if (typeof rawIds === "string") {
    const text = rawIds.trim();
    if (text.startsWith("[")) {
      try {
        rawIds = JSON.parse(text);
      } catch {
        return { ok: false, error: "Daftar produk tidak terbaca." };
      }
    } else {
      rawIds = text.split(",");
    }
  }
  if (!Array.isArray(rawIds)) {
    return { ok: false, error: "Pilih minimal satu produk." };
  }
  const seen = new Set<string>();
  const productIds: string[] = [];
  for (const v of rawIds) {
    const id = String(v ?? "").trim().toLowerCase();
    if (!id) continue;
    if (!UUID_RE.test(id)) return { ok: false, error: "Daftar produk tidak valid." };
    if (seen.has(id)) continue;
    seen.add(id);
    productIds.push(id);
  }
  if (productIds.length === 0) {
    return { ok: false, error: "Pilih minimal satu produk." };
  }
  if (productIds.length > DROPSHIP_MAX_PRODUCTS_PER_EXPORT) {
    return {
      ok: false,
      error: `Maksimal ${DROPSHIP_MAX_PRODUCTS_PER_EXPORT} produk per unduhan (dipilih ${productIds.length}). Bagi jadi beberapa unduhan ya.`,
    };
  }
  const markupPct = parseMarkupInput(fields.markup);
  if (markupPct === null) {
    return {
      ok: false,
      error: `Markup harus bilangan bulat ${DROPSHIP_MARKUP_MIN}–${DROPSHIP_MARKUP_MAX}%.`,
    };
  }
  const dryRun = ["1", "true", "on", "yes"].includes(
    String(fields.dryRun ?? "").trim().toLowerCase()
  );
  return { ok: true, value: { productIds, markupPct, dryRun } };
}

// ── Template Mass Upload milik mitra (keputusan 6) ───────────────────────────

/** Jalur unduh template di Seller Centre versi web. */
export const DROPSHIP_TEMPLATE_PATH_LABEL =
  "Produk Saya → Pengaturan Massal → Mass Upload → Download Template";

/** Pesan template belum dilampirkan — layar (sebelum kirim) dan route (400) sama. */
export const DROPSHIP_TEMPLATE_REQUIRED_COPY = `Pilih dulu file template Mass Upload dari Seller Centre kamu sendiri (${DROPSHIP_TEMPLATE_PATH_LABEL}).`;

export type DropshipTemplateCheck<T> =
  | { ok: true; file: T }
  | { ok: false; error: string; needsTemplate: boolean };

/**
 * Gerbang field `template` export mitra: WAJIB dilampirkan — tak ada cadangan
 * template toko — dan tak melebihi `maxBytes`. Keterbacaan isinya (.xlsx Mass
 * Upload) diperiksa terpisah oleh pembaca template di route.
 */
export function checkDropshipTemplate<T extends { size: number }>(
  file: T | null | undefined,
  maxBytes: number
): DropshipTemplateCheck<T> {
  if (!file || !Number.isFinite(file.size) || file.size <= 0) {
    return { ok: false, error: DROPSHIP_TEMPLATE_REQUIRED_COPY, needsTemplate: true };
  }
  if (file.size > maxBytes) {
    return {
      ok: false,
      error: `Template terlalu besar (maks ${Math.round(maxBytes / 1024 / 1024)} MB).`,
      needsTemplate: false,
    };
  }
  return { ok: true, file };
}

// ── Daftar produk di layar ───────────────────────────────────────────────────

/** Satu kartu produk di pemilih (dipulangkan route products). */
export type DropshipProductItem = {
  id: string;
  name: string;
  brand: string | null;
  coverUrl: string | null;
  /** Harga normal termurah–termahal di antara varian aktif berharga. */
  priceMin: number | null;
  priceMax: number | null;
  /** Σ stok bebas varian aktif (sudah dikurangi cadangan toko). */
  stock: number;
  variantCount: number;
};

export type DropshipProductsResponse = {
  items: DropshipProductItem[];
  page: number;
  pageSize: number;
  /** Ada halaman berikutnya? (bukan hitungan total — hindari `count` yang menelan error). */
  hasMore: boolean;
};

type VariantLike = Pick<ShopeeVariantRow, "price_normal" | "price_extra" | "stock" | "is_active">;

/** Rentang harga + stok bebas dari embed varian mentah (varian aktif saja). */
export function summarizeDropshipVariants(
  variants: readonly VariantLike[] | null | undefined,
  stockBuffer: number
): Pick<DropshipProductItem, "priceMin" | "priceMax" | "stock" | "variantCount"> {
  let priceMin: number | null = null;
  let priceMax: number | null = null;
  let stock = 0;
  let variantCount = 0;
  for (const v of variants ?? []) {
    if (v.is_active === false) continue;
    variantCount += 1;
    // mig 7900: rentang harga utuh (dasar + tambahan) — sama dengan harga file.
    const price = shopeeVariantPrice(v) ?? NaN;
    if (Number.isFinite(price) && price > 0) {
      priceMin = priceMin === null ? price : Math.min(priceMin, price);
      priceMax = priceMax === null ? price : Math.max(priceMax, price);
    }
    stock += resellerVisibleStock(Number(v.stock ?? 0), stockBuffer);
  }
  return { priceMin, priceMax, stock, variantCount };
}

/** Rentang harga jual setelah markup, untuk teks kartu "Rp 85.000 → Rp 102.000". */
export function dropshipPriceRange(
  item: Pick<DropshipProductItem, "priceMin" | "priceMax">,
  markupPct: number
): { min: number; max: number } | null {
  if (item.priceMin === null || item.priceMax === null) return null;
  return {
    min: applyDropshipMarkup(item.priceMin, markupPct),
    max: applyDropshipMarkup(item.priceMax, markupPct),
  };
}

/** Contoh harga untuk pratinjau: maks 3 produk berharga, urut harga termurah. */
export function dropshipSamplePrices(
  items: readonly Pick<DropshipProductItem, "name" | "priceMin">[],
  markupPct: number,
  limit = 3
): { name: string; base: number; final: number }[] {
  return items
    .filter((i): i is typeof i & { priceMin: number } => i.priceMin !== null && i.priceMin > 0)
    .sort((a, b) => a.priceMin - b.priceMin)
    .slice(0, Math.max(0, limit))
    .map((i) => ({
      name: i.name,
      base: i.priceMin,
      final: applyDropshipMarkup(i.priceMin, markupPct),
    }));
}

/** Pencarian klien atas daftar yang sudah dimuat: nama atau merek, tanpa huruf besar-kecil. */
export function filterDropshipProducts<T extends Pick<DropshipProductItem, "name" | "brand">>(
  items: readonly T[],
  query: string
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items];
  return items.filter(
    (i) => i.name.toLowerCase().includes(q) || (i.brand ?? "").toLowerCase().includes(q)
  );
}

// ── Catatan riwayat ──────────────────────────────────────────────────────────

/** Catatan satu baris untuk `dropship_shopee_logs.note` (export). */
export function dropshipExportLogNote(s: {
  productCount: number;
  rowCount: number;
  fileCount: number;
  skippedProducts: number;
  markupPct: number;
}): string {
  const parts = [
    `${s.productCount} produk`,
    `${s.rowCount} baris`,
    `${s.fileCount} file`,
    `markup ${s.markupPct}%`,
  ];
  if (s.skippedProducts > 0) parts.push(`${s.skippedProducts} produk dilewati`);
  return parts.join(" · ");
}
